import React, { useState, useMemo } from 'react';
import { Container } from '@/components/common/container';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { KeenIcon } from '@/components/keenicons';
import { usePermissionGroups, usePermissionStatistics } from '@/hooks/admin/usePermissions';

/**
 * Permissions List Page
 *
 * Route: /admin/permissions
 * Access: Admins with Identity.Permissions.Read permission
 *
 * Features:
 * - View all available permissions
 * - Group by provider/resource
 * - Search permissions
 * - Collapsible groups
 * - Permission statistics
 *
 * Spec reference: Lines 743-869 in 03-AUTH-AUTHORIZATION-SPECS.md
 */
export function PermissionsListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const { data: permissionGroups, isLoading, error } = usePermissionGroups();
  const { data: stats } = usePermissionStatistics();

  // Filter permissions by search query
  const filteredGroups = useMemo(() => {
    if (!permissionGroups || !searchQuery.trim()) return permissionGroups || [];

    const query = searchQuery.toLowerCase();
    return permissionGroups
      .map((group) => ({
        ...group,
        permissions: group.permissions.filter(
          (p) =>
            p.name.toLowerCase().includes(query) ||
            p.displayName.toLowerCase().includes(query) ||
            p.description?.toLowerCase().includes(query)
        ),
      }))
      .filter((group) => group.permissions.length > 0);
  }, [permissionGroups, searchQuery]);

  // Auto-expand groups with search results
  React.useEffect(() => {
    if (searchQuery.trim() && filteredGroups) {
      setExpandedGroups(new Set(filteredGroups.map((g) => g.name)));
    }
  }, [searchQuery, filteredGroups]);

  const toggleGroupExpanded = (groupName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const toggleAllGroups = () => {
    if (expandedGroups.size === filteredGroups?.length) {
      setExpandedGroups(new Set());
    } else {
      setExpandedGroups(new Set(filteredGroups?.map((g) => g.name) || []));
    }
  };

  return (
    <Container>
      <div className="flex flex-col gap-6 py-6">
        {/* Page Header */}
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold tracking-tight">Permission Management</h1>
          <p className="text-muted-foreground">
            Browse all available permissions in the system
          </p>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardContent className="flex items-center gap-4 pt-6">
                <div className="rounded-lg bg-blue-50 p-3">
                  <KeenIcon icon="shield-tick" className="text-2xl text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Total Permissions</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center gap-4 pt-6">
                <div className="rounded-lg bg-green-50 p-3">
                  <KeenIcon icon="element-11" className="text-2xl text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.providerCount}</p>
                  <p className="text-xs text-muted-foreground">Permission Groups</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center gap-4 pt-6">
                <div className="rounded-lg bg-purple-50 p-3">
                  <KeenIcon icon="graph-up" className="text-2xl text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {stats.providers[0]?.name || '—'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Largest Group ({stats.providers[0]?.count || 0} perms)
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search and Controls */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative flex-1">
                <KeenIcon
                  icon="magnifier"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  type="search"
                  placeholder="Search permissions by name or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              {filteredGroups && filteredGroups.length > 0 && (
                <button
                  onClick={toggleAllGroups}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                >
                  <KeenIcon
                    icon={expandedGroups.size === filteredGroups.length ? 'up' : 'down'}
                  />
                  {expandedGroups.size === filteredGroups.length
                    ? 'Collapse All'
                    : 'Expand All'}
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {isLoading && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <KeenIcon icon="arrows-circle" className="mb-4 animate-spin text-4xl text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading permissions...</p>
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {error && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <KeenIcon icon="information-circle" className="mb-4 text-4xl text-destructive" />
              <h3 className="mb-2 text-lg font-semibold">Failed to load permissions</h3>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!isLoading && !error && filteredGroups && filteredGroups.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <KeenIcon icon="shield-cross" className="mb-4 text-4xl text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">No permissions found</h3>
              <p className="text-sm text-muted-foreground">
                {searchQuery
                  ? 'Try adjusting your search criteria'
                  : 'No permissions are available in the system'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Permission Groups */}
        {!isLoading && !error && filteredGroups && filteredGroups.length > 0 && (
          <div className="space-y-3">
            {filteredGroups.map((group) => (
              <Card key={group.name}>
                <Collapsible
                  open={expandedGroups.has(group.name)}
                  onOpenChange={() => toggleGroupExpanded(group.name)}
                >
                  <CardHeader className="cursor-pointer hover:bg-muted/50">
                    <CollapsibleTrigger className="flex w-full items-center justify-between">
                      <div className="flex items-center gap-3 text-left">
                        <KeenIcon
                          icon={expandedGroups.has(group.name) ? 'down' : 'right'}
                          className="text-muted-foreground transition-transform"
                        />
                        <div>
                          <CardTitle className="text-base">{group.displayName}</CardTitle>
                          <CardDescription>
                            {group.permissions.length} permission
                            {group.permissions.length !== 1 ? 's' : ''}
                          </CardDescription>
                        </div>
                      </div>
                      <Badge variant="secondary">{group.permissions.length}</Badge>
                    </CollapsibleTrigger>
                  </CardHeader>

                  <CollapsibleContent>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Permission Name</TableHead>
                              <TableHead>Description</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.permissions.map((permission) => (
                              <TableRow key={permission.name}>
                                <TableCell>
                                  <div className="flex flex-col gap-1">
                                    <span className="font-medium">
                                      {permission.displayName}
                                    </span>
                                    <code className="text-xs text-muted-foreground">
                                      {permission.name}
                                    </code>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <span className="text-sm text-muted-foreground">
                                    {permission.description || '—'}
                                  </span>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Container>
  );
}
